/* @flow */
import _ from 'lodash';
import cx from 'classnames';
import React from 'react';

import ChatMessage from 'entities/ChatMessage';
import ChatActions from 'chat/actions/ChatActions';
import ChatStore from 'chat/stores/ChatStore';
import getParticipantNames from 'chat/utils/getParticipantNames';
import Group from 'entities/Group';
import Link from 'components/Link.jsx';
import linkStateWithStores from 'components/mixins/linkStateWithStores';
import MessageStore from 'groups/stores/MessageStore';
import UnreadStore from 'stores/UnreadStore';
import type { ChatTypeType, OtherMemberType } from 'entities/Chat';
import ChatListItemAvatar from 'chat/components/ChatListItemAvatar.jsx';
import ChatListItemContentConversation from 'chat/components/ChatListItemContentConversation.jsx';
import ChatListItemContentNew from 'chat/components/ChatListItemContentNew.jsx';

import './styles/ChatListItem.scss';

// typecheck: ignore file
type State = {
  chatColor?: string,
  otherMemberColor: string,
  otherMemberInitials: string,
  groupSummaries: any[],
  inactive: boolean,
  isDisabled: boolean,
  chatType: ?ChatTypeType,
  activityStartAt: ?Date,
  title: string,
  isNew: boolean,
  lastMessage: ?ChatMessage,
  participantNames: string[],
  unreadCount: number,
  timestamp: Date,
};

export const ChatListItem = React.createClass({
  propTypes: {
    chatUuid: React.PropTypes.string,
    query: React.PropTypes.string,
    withinGroupId: React.PropTypes.number,
    selected: React.PropTypes.bool,
    group: React.PropTypes.instanceOf(Group).isRequired,
    onClick: React.PropTypes.func,
  },

  contextTypes: {
    router: React.PropTypes.object.isRequired,
    chatSelectionAction: React.PropTypes.string,
  },

  mixins: [
    linkStateWithStores([ChatStore, UnreadStore, MessageStore]),
  ],

  // typecheck: ignore statement
  getInitialState(): State {
    return this.getStateFromStores();
  },

  shouldComponentUpdate(nextProps: Object, nextState: Object) {
    const { chatUuid } = nextProps;

    const chat = chatUuid ? ChatStore.getChat(chatUuid) : ChatStore.getLocal();
    if (!chat) {
      return false;
    }

    // props
    if (this.props.chatUuid !== nextProps.chatUuid ||
        this.props.selected !== nextProps.selected ||
        this.props.query !== nextProps.query ||
        this.props.withinGroupId !== nextProps.withinGroupId ||
        this.props.group !== nextProps.group) {
      return true;
    }

    // shallow state
    const shallowKeys = [
      'isDisabled',
      'inactive',
      'isNew',
      'chatColor',
      'chatType',
      'otherMemberInitials',
      'otherMemberColor',
      'unreadCount',
    ];

    for (const key of shallowKeys) {
      if (this.state[key] !== nextState[key]) {
        return true;
      }
    }

    // state that can't be compared shallow, because our Chat entity
    // is Backbone
    if (!this.state.lastMessage && nextState.lastMessage) {
      return true;
    }

    if (this.state.lastMessage && nextState.lastMessage &&
      this.state.lastMessage.id !== nextState.lastMessage.id) {
      return true;
    }

    if (!_.isEqual(this.state.groupSummaries, nextState.groupSummaries)) {
      return true;
    }

    if (!_.isEqual(this.state.participantNames, nextState.participantNames)) {
      return true;
    }

    return false;
  },

  getStateFromStores() {
    const { chatUuid } = this.props;

    // For new chat threads, the chatUuid parameter is not present in the url.
    // In this case, We invoke ChatStore.getLocal to get the Chat representing a new thread.
    // The new, local-only, Chat must exist in ChatStore prior to this invocation.
    const chat = chatUuid ? ChatStore.getChat(chatUuid) : ChatStore.getLocal();

    // XXX There must be a better way to do this
    // For removing a new chat thread... getStateFromStores is invoked after Chat is deleted
    if (!chat) {
      return {
        isDisabled: true,
        groupSummaries: [],
        inactive: true,
        chatType: null,
        isNew: true,
        title: '',
        lastMessage: null,
        chatColor: '',
        otherMemberInitials: '',
        otherMemberColor: '',
        participantNames: [],
        unreadCount: 0,
        activityStartAt: null,
        timestamp: new Date(),
      };
    }

    const activityStartAt = chat.getActivity() ?
      chat.getActivity().getStartAt() :
      undefined;

    const otherMember: ?OtherMemberType = chat.getOtherMember();

    return {
      isDisabled: chat.isDisabled(),
      groupSummaries: chat.get('group_summaries'),
      inactive: chat.isInactive(),
      chatType: chat.getType(),
      isNew: chat.isNew(), // this state value causes render to call renderNewChatListItem
      title: chat.getTitle(),
      lastMessage: chat.getLastMessage(),
      chatColor: chat && chat.getColor() || '',
      otherMemberInitials: otherMember ? otherMember.initials : '',
      otherMemberColor: otherMember ? otherMember.color : '',
      participantNames: getParticipantNames(chat),
      unreadCount: chatUuid ? UnreadStore.getUnreadCount(chatUuid) : 0,
      activityStartAt,
      timestamp: chat.getSortKeyTimestamp(),
    };
  },

  resetUnreadCount() {
    this.setState({ unreadCount: 0 });
  },

  selectChat() {
    const { chatUuid } = this.props;
    if (chatUuid) {
      ChatActions.selectChat(chatUuid);
    }
  },

  // Upon clicking the 'x' icon to dismiss a new chat. Destroys the local chat thread
  // and redirects to the default message thread in the focused group.
  closeNewChat() {
    const groupCode = this.props.group.getCode();
    this.context.router.replace(`/classes/${groupCode}/messages/`);
    ChatActions.destroyLocal();
  },

  buildAvatar() {
    const {
      chatColor,
      otherMemberInitials,
      otherMemberColor,
      inactive,
      chatType,
      activityStartAt,
    } = this.state;

    return (
      <ChatListItemAvatar
        chatColor={chatColor}
        otherMemberInitials={otherMemberInitials}
        otherMemberColor={otherMemberColor}
        inactive={inactive}
        chatType={chatType}
        activityStartAt={activityStartAt}
      />
    );
  },

  renderNewChatListItem() {
    return (
      <li className="ChatListItem">
        <Link
          className={cx({
            'ChatListItem-link': true,
            'ChatListItem-newChat': true,
            'ChatListItem-selected': this.props.selected,
          })}
          params={{}}>
          <ChatListItemContentNew
            avatar={this.buildAvatar()}
            participantNames={this.state.participantNames}
            closeNewChat={this.closeNewChat}
          />
        </Link>
      </li>
    );
  },

  render() {
    const { chatUuid, query, group } = this.props;

    // XXX these should be different components
    if (this.state.isNew || !chatUuid) {
      return this.renderNewChatListItem();
    }

    let linkTo = `/classes/${group.getCode()}/messages/${chatUuid}`;
    let onClick = this.resetUnreadCount;

    if (this.context.chatSelectionAction === 'store') {
      linkTo = null;
      onClick = () => {
        this.resetUnreadCount();
        this.selectChat();
        if (this.props.onClick) {
          this.props.onClick();
        }
      };
    }

    return (
      <li className="ChatListItem">
        <Link
          to={linkTo}
          onClick={onClick}
          className={cx({
            'ChatListItem-link': true,
            'ChatListItem-hasUnread': this.state.unreadCount > 0,
            'ChatListItem-inactive': this.state.inactive,
            'ChatListItem-selected': this.props.selected,
          })}>
          <ChatListItemContentConversation
            avatar={this.buildAvatar()}
            isDisabled={this.state.isDisabled}
            query={query}
            title={this.state.title}
            lastMessage={this.state.lastMessage}
            timestamp={this.state.timestamp}
          />
        </Link>
      </li>
    );
  },
});

export default ChatListItem;



// WEBPACK FOOTER //
// ./src/chat/components/ChatListItem.jsx